import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Shield, Handshake, Globe, BarChart3, Building2, Heart, Factory, Laptop, TrendingUp, ShoppingCart, Lock, CheckCircle } from 'lucide-react'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'
import { MotionTokens } from '../styles/motion-tokens'

const brandFont = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
  fontWeight: 100,
} as const

const bodyFont = {
  fontFamily: '"Helvetica Neue", sans-serif',
  fontWeight: 300,
} as const

const sectionReveal = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: {
    duration: MotionTokens.sectionReveal.duration,
    ease: [0.22, 1, 0.32, 1],
  },
}

function GlassCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border bg-white/[0.055] border-white/[0.09] backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/[0.14] transition-all duration-300 ${className}`}>
      {children}
    </div>
  )
}

export default function HomePage() {
  return (
    <div className="min-h-screen">
      <SiteHeader />

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6">
        <div className="relative z-10 text-center max-w-6xl mx-auto">
          {/* Staggered hero entrance — Shield, h1, subtitle, CTA at 120ms intervals */}
          <motion.div
            className="mb-6 flex justify-center"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0, ease: [0.22, 1, 0.36, 1] }}
          >
            <Shield className="w-16 h-16 text-trust-blue" strokeWidth={1} />
          </motion.div>

          <motion.h1
            className="text-5xl md:text-7xl mb-6 text-white tracking-[0.15em]"
            style={brandFont}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          >
            Consciousness AI,
            <br />
            <span className="text-transparent bg-clip-text bg-trust-gradient">
              Bound by Ethics
            </span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
          >
            <p
              className="text-xl md:text-2xl tracking-wide mb-4 text-white/70 max-w-4xl mx-auto"
              style={bodyFont}
            >
              Enterprise solutions built on the LUKHAS Constellation Framework
            </p>

            <p
              className="text-lg md:text-xl tracking-wide mb-12 text-white/50 max-w-3xl mx-auto"
              style={bodyFont}
            >
              LUKHAS combines symbolic reasoning with Guardian-enforced ethics — delivering
              auditable, compliant AI that your legal and compliance teams can stand behind.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.36, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Link
                to="/enterprise"
                className="px-8 py-3 rounded-full bg-trust-blue text-white text-sm tracking-[0.06em] hover:bg-trust-blue/90 transition-all"
                style={bodyFont}
              >
                Explore Enterprise Solutions
              </Link>
              <Link
                to="/solutions"
                className="px-8 py-3 rounded-full border border-trust-blue/40 text-trust-blue text-sm tracking-[0.06em] hover:border-trust-blue/70 transition-all"
                style={bodyFont}
              >
                View Industry Solutions
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center gap-8 text-sm text-white/50 tracking-wide" style={bodyFont}>
              <span className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-trust-blue rounded-full" />
                Enterprise-Ready Architecture
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-trust-blue/50 rounded-full" />
                SOC 2 Type II (target Q3 2026)
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-trust-blue/50 rounded-full" />
                ISO 27001 (target Q4 2026)
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-trust-blue rounded-full" />
                Guardian Compliance 99.7%
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Company Vision */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div {...sectionReveal}>
              <h2
                className="text-4xl md:text-5xl tracking-[0.1em] mb-6 text-white"
                style={brandFont}
              >
                The <span className="text-trust-blue">LUKHAS</span> Mission
              </h2>
              <p className="text-xl text-white/70 max-w-4xl mx-auto" style={bodyFont}>
                Building the trusted infrastructure for conscious technology that serves humanity
                while advancing business innovation.
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: 'Ethical Leadership', desc: 'Guardian AI ensures consciousness technology serves humanity' },
              { icon: Handshake, title: 'Enterprise Partnership', desc: 'Building the future of enterprise AI together' },
              { icon: Globe, title: 'Global Innovation', desc: 'Pioneering AI consciousness across six continents' },
              { icon: BarChart3, title: 'Transparent Governance', desc: 'Open oversight and ethical accountability' },
            ].map(({ icon: Icon, title, desc }) => (
              <GlassCard key={title}>
                <div className="p-6 text-center">
                  <div className="mb-4 flex justify-center">
                    <Icon className="w-10 h-10 text-trust-blue" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg tracking-wide mb-2 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                    {title}
                  </h3>
                  <p className="text-sm text-white/50" style={bodyFont}>{desc}</p>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Overview */}
      <section id="solutions" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div {...sectionReveal}>
              <h2
                className="text-4xl md:text-5xl tracking-[0.1em] mb-6 text-white"
                style={brandFont}
              >
                Industry <span className="text-trust-blue">Solutions</span>
              </h2>
              <p className="text-xl text-white/70 max-w-3xl mx-auto" style={bodyFont}>
                Consciousness technology tailored to your industry&apos;s unique challenges and opportunities
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Building2, title: 'Financial Services', desc: 'Risk assessment, fraud detection, regulatory compliance, and intelligent trading systems.', items: ['Real-time risk scoring and monitoring', 'Automated regulatory compliance', 'Advanced fraud pattern recognition'] },
              { icon: Heart, title: 'Healthcare', desc: 'Clinical decision support, patient communication, diagnostic assistance, and care optimization.', items: ['HIPAA-compliant diagnostic support', 'Personalized treatment planning', 'Patient outcome prediction'] },
              { icon: Factory, title: 'Manufacturing', desc: 'Process optimization, quality control, predictive maintenance, and supply chain intelligence.', items: ['Predictive equipment maintenance', 'Real-time quality assurance', 'Supply chain optimization'] },
              { icon: Laptop, title: 'Technology', desc: 'Software development acceleration, system architecture, code review, and innovation consultation.', items: ['AI-assisted code generation', 'Architecture pattern recognition', 'Automated security vulnerability detection'] },
              { icon: TrendingUp, title: 'Consulting', desc: 'Strategic planning, organizational transformation, change management, and decision intelligence.', items: ['Strategic scenario modeling', 'Organizational impact analysis', 'Change readiness assessment'] },
              { icon: ShoppingCart, title: 'Retail & E-Commerce', desc: 'Customer personalization, demand forecasting, inventory optimization, and market intelligence.', items: ['Hyper-personalized recommendations', 'Dynamic pricing optimization', 'Demand forecasting and inventory'] },
            ].map(({ icon: Icon, title, desc, items }) => (
              <GlassCard key={title}>
                <div className="p-8">
                  <div className="mb-4">
                    <Icon className="w-10 h-10 text-trust-blue" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-2xl tracking-wide mb-4 text-trust-blue" style={{ ...bodyFont, fontWeight: 300 }}>
                    {title}
                  </h3>
                  <p className="text-white/70 mb-4" style={bodyFont}>{desc}</p>
                  <ul className="space-y-2 text-white/50 text-sm">
                    {items.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="text-trust-blue mt-0.5">&#10003;</span>
                        <span style={bodyFont}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </GlassCard>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/solutions"
              className="px-8 py-3 rounded-full bg-trust-blue text-white text-sm tracking-[0.06em] hover:bg-trust-blue/90 transition-all inline-block"
              style={bodyFont}
            >
              Explore All Solutions
            </Link>
          </div>
        </div>
      </section>

      {/* Security & Compliance */}
      <section id="security" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div {...sectionReveal}>
              <h2
                className="text-4xl md:text-5xl tracking-[0.1em] mb-6 text-white"
                style={brandFont}
              >
                Enterprise <span className="text-trust-blue">Security</span> & Compliance
              </h2>
              <p className="text-xl text-white/70 max-w-3xl mx-auto" style={bodyFont}>
                Guardian-enforced protection with comprehensive compliance across global regulations
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Lock, title: 'Multi-Layer Security', desc: 'Defense-in-depth architecture with encryption at rest and in transit', items: ['AES-256-GCM encryption', 'TLS 1.3 protocols', 'Zero-trust architecture'] },
              { icon: CheckCircle, title: 'Compliance Roadmap', desc: 'Actively pursuing international data protection certifications', items: ['SOC 2 Type II (target Q3 2026)', 'ISO 27001 (target Q4 2026)', 'GDPR & CCPA privacy controls'] },
              { icon: Shield, title: 'Guardian Oversight', desc: 'Constitutional AI continuously monitors for ethical and security compliance', items: ['24/7 threat monitoring', 'Ethical drift detection', 'Automated incident response'] },
            ].map(({ icon: Icon, title, desc, items }) => (
              <GlassCard key={title}>
                <div className="p-8 text-center">
                  <div className="mb-4 flex justify-center">
                    <Icon className="w-10 h-10 text-trust-blue" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl tracking-wide mb-3 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                    {title}
                  </h3>
                  <p className="text-white/50 mb-4" style={bodyFont}>{desc}</p>
                  <div className="space-y-1 text-sm text-white/40">
                    {items.map((item) => (
                      <p key={item} style={bodyFont}>{item}</p>
                    ))}
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership CTA */}
      <section className="py-24 px-6">
        <GlassCard className="max-w-4xl mx-auto">
          <div className="p-12 text-center">
            <h2
              className="text-4xl md:text-5xl tracking-[0.1em] mb-6 text-white"
              style={brandFont}
            >
              Ready to Transform Your Enterprise?
            </h2>
            <p className="text-xl text-white/70 mb-12" style={bodyFont}>
              Discover enterprise-grade consciousness technology built for the future
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-12 py-3 rounded-full bg-trust-blue text-white text-sm tracking-[0.06em] hover:bg-trust-blue/90 transition-all"
                style={bodyFont}
              >
                Schedule a Demo
              </Link>
              <Link
                to="/enterprise"
                className="px-12 py-3 rounded-full border border-trust-blue/40 text-trust-blue text-sm tracking-[0.06em] hover:border-trust-blue/70 transition-all"
                style={bodyFont}
              >
                View Enterprise Features
              </Link>
            </div>
          </div>
        </GlassCard>
      </section>

      <SiteFooter />
    </div>
  )
}
